import React from "react";
import { Card } from "@/components/ui";
import { Review } from "@/services/bedrock";
import { getSourceIcon, formatRelativeTime } from "./utils";
import { ReviewRating } from "./ReviewRating";
import { ReplyStatus } from "./ReplyStatus";

interface ReviewSummaryCardProps {
  review: Review;
  isSelected: boolean;
  onClick: () => void;
}

export function ReviewSummaryCard({ review, isSelected, onClick }: ReviewSummaryCardProps) {
  const isAnonymous = !review.displayName;
  const hasReplied = review.replied;
  const replySkipped = review.replySkipped;

  return (
    <Card
      onClick={onClick}
      className={`group py-3.5 pr-3.5 cursor-pointer relative transition-all duration-200 flex flex-col gap-2 shrink-0 border rounded-xl transform ${
        isSelected
          ? "bg-indigo-50/40 border-slate-400 border-l-4 border-l-slate-600 pl-[11px] shadow-sm transform scale-[1.01]"
          : `${
              hasReplied || replySkipped ? "bg-slate-50/40" : "bg-white"
            } border-slate-200 pl-3.5 hover:bg-slate-50/20 hover:border-l-4 hover:border-l-slate-300 hover:pl-[11px] hover:border-y-slate-300 hover:border-r-slate-300 hover:shadow-md hover:-translate-y-[2px]`
      }`}
    >
      {/* Top Row: Icon + Name (Left) and Rating + Time (Right) */}
      <div className="flex items-start justify-between gap-3">
        {/* Left Side: Identity Column */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2">
            <div className="shrink-0 transition-opacity duration-200 opacity-90 group-hover:opacity-100">
              {getSourceIcon(review.sourceSystem)}
            </div>
            <span
              className={`text-sm truncate leading-tight ${
                isAnonymous
                  ? "italic text-slate-400 font-medium"
                  : isSelected
                  ? "font-bold text-indigo-950"
                  : "font-bold text-slate-800"
              }`}
            >
              {review.displayName || "Anonymous"}
            </span>
          </div>

          <div className="flex">
            <ReplyStatus review={review} />
          </div>
        </div>

        {/* Right Side: Metrics */}
        <div className="flex flex-col items-end shrink-0 gap-1 mt-0.5">
          <ReviewRating review={review} size="sm" fallback="-" />
          <div
            className={`flex items-center gap-1 transition-colors duration-200 ${
              isSelected ? "opacity-70" : "text-slate-400 group-hover:text-slate-500"
            }`}
          >
            <span className="text-[10px] font-bold">
              {formatRelativeTime(review.effectiveDate || review.publishedAt)}
            </span>
          </div>
        </div>
      </div>

      {/* Content Preview */}
      {review.content ? (
        <p
          className={`text-xs leading-relaxed line-clamp-2 mt-0.5 pr-2 ${
            isSelected ? "font-medium text-indigo-900/90" : "font-medium text-slate-600"
          }`}
        >
          {review.content}
        </p>
      ) : (
        <p
          className={`text-xs leading-relaxed line-clamp-2 mt-0.5 ${
            isSelected ? "font-medium italic text-indigo-900/60" : "font-medium italic text-slate-400"
          }`}
        >
          No comment was submitted.
        </p>
      )}
    </Card>
  );
}
